import { useEffect, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { HOLDING_KINDS, searchCoins, searchStocks } from "../lib/prices";

const label = "text-[10px] uppercase tracking-widest text-nest-muted block mb-1";

function useSearch(query, searchFn) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const r = await searchFn(query);
        if (!cancelled) setResults(r);
      } catch {
        if (!cancelled) setError(navigator.onLine ? "Search failed" : "You're offline");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, searchFn]);

  return { results, loading, error };
}

function Picker({ placeholder, searchFn, onPick, render, testId }) {
  const [query, setQuery] = useState("");
  const { results, loading, error } = useSearch(query, searchFn);
  return (
    <div>
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-nest-faint" />
        <input
          className="nest-input w-full !pl-9"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid={testId}
        />
        {loading && (
          <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-nest-faint" />
        )}
      </div>
      {error && <div className="text-xs text-nest-warn mt-2">{error}</div>}
      {results.length > 0 && (
        <div className="mt-2 border border-nest-line rounded-2xl overflow-hidden bg-nest-surface-4 max-h-60 overflow-y-auto">
          {results.map((r, i) => (
            <button
              type="button"
              key={i}
              onClick={() => {
                onPick(r);
                setQuery("");
              }}
              className="w-full text-left px-4 py-3 text-sm border-t first:border-t-0 border-nest-line hover:bg-nest-surface-2"
            >
              {render(r)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function blank(kind) {
  return { kind, name: "", amount: "", quantity: "", metal: "gold", unit: "g" };
}

export default function HoldingForm({ initial, onSave, onDelete }) {
  const [h, setH] = useState(() => (initial ? { ...blank(initial.kind), ...initial } : blank("stock")));
  const set = (patch) => setH((prev) => ({ ...prev, ...patch }));

  const ready =
    (h.kind === "cash" && h.name.trim() && h.amount !== "") ||
    (h.kind === "crypto" && h.coinId && h.quantity !== "") ||
    (h.kind === "metal" && h.quantity !== "") ||
    (h.kind === "stock" && h.symbol && h.quantity !== "");

  const submit = (e) => {
    e.preventDefault();
    if (!ready) return;
    const base = { id: h.id || crypto.randomUUID(), kind: h.kind };
    let out;
    if (h.kind === "cash") {
      out = { ...base, name: h.name.trim(), amount: parseFloat(h.amount) || 0 };
    } else if (h.kind === "crypto") {
      out = {
        ...base,
        name: h.name.trim() || h.coinName,
        coinId: h.coinId,
        coinName: h.coinName,
        coinSymbol: h.coinSymbol,
        quantity: parseFloat(h.quantity) || 0,
      };
    } else if (h.kind === "metal") {
      out = {
        ...base,
        name: h.name.trim() || (h.metal === "gold" ? "Gold" : "Silver"),
        metal: h.metal,
        unit: h.unit,
        quantity: parseFloat(h.quantity) || 0,
      };
    } else {
      out = {
        ...base,
        name: h.name.trim() || h.symbolName || h.symbol,
        symbol: h.symbol,
        symbolName: h.symbolName,
        quantity: parseFloat(h.quantity) || 0,
      };
    }
    onSave(out);
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="holding-form">
      <div>
        <span className={label}>Type</span>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(HOLDING_KINDS).map(([k, text]) => (
            <button
              type="button"
              key={k}
              onClick={() => setH({ ...blank(k), id: h.id })}
              data-testid={`holding-kind-${k}`}
              className={`py-2.5 rounded-xl text-sm border transition-colors ${
                h.kind === k
                  ? "bg-nest-brand text-white border-nest-brand"
                  : "bg-nest-surface-2 border-nest-line text-nest-soft"
              }`}
            >
              {text}
            </button>
          ))}
        </div>
      </div>

      {h.kind === "stock" && (
        <div>
          <span className={label}>Share, ETF or fund</span>
          {h.symbol ? (
            <div className="flex items-center justify-between bg-nest-surface-3 border border-nest-line rounded-xl px-4 py-3 text-sm">
              <div>
                <div className="font-medium">{h.symbol}</div>
                <div className="text-xs text-nest-muted">{h.symbolName}</div>
              </div>
              <button type="button" className="text-xs text-nest-brand" onClick={() => set({ symbol: "", symbolName: "" })}>
                Change
              </button>
            </div>
          ) : (
            <Picker
              placeholder="Search name or ticker, e.g. VUAG or Vanguard"
              searchFn={searchStocks}
              testId="stock-search-input"
              onPick={(r) => set({ symbol: r.symbol, symbolName: r.name })}
              render={(r) => (
                <>
                  <div className="font-medium">
                    {r.symbol} <span className="text-nest-faint font-normal">· {r.exchange}</span>
                  </div>
                  <div className="text-xs text-nest-muted">
                    {r.name} · {r.type}
                  </div>
                </>
              )}
            />
          )}
          <p className="text-[11px] text-nest-muted mt-2">
            UK listings end in <b>.L</b> (e.g. VUAG.L). Pick the one your platform holds.
          </p>
        </div>
      )}

      {h.kind === "crypto" && (
        <div>
          <span className={label}>Coin</span>
          {h.coinId ? (
            <div className="flex items-center justify-between bg-nest-surface-3 border border-nest-line rounded-xl px-4 py-3 text-sm">
              <div className="font-medium">
                {h.coinName} <span className="text-nest-faint uppercase">{h.coinSymbol}</span>
              </div>
              <button type="button" className="text-xs text-nest-brand" onClick={() => set({ coinId: "" })}>
                Change
              </button>
            </div>
          ) : (
            <Picker
              placeholder="Search, e.g. Bitcoin"
              searchFn={searchCoins}
              testId="coin-search-input"
              onPick={(c) => set({ coinId: c.id, coinName: c.name, coinSymbol: c.symbol })}
              render={(c) => (
                <div className="flex items-center gap-2">
                  {c.thumb && <img src={c.thumb} alt="" className="w-5 h-5 rounded-full" />}
                  <span className="font-medium">{c.name}</span>
                  <span className="text-nest-faint uppercase">{c.symbol}</span>
                </div>
              )}
            />
          )}
        </div>
      )}

      {h.kind === "metal" && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className={label}>Metal</span>
            <select className="nest-input w-full" value={h.metal} onChange={(e) => set({ metal: e.target.value })}>
              <option value="gold">Gold</option>
              <option value="silver">Silver</option>
            </select>
          </div>
          <div>
            <span className={label}>Unit</span>
            <select className="nest-input w-full" value={h.unit} onChange={(e) => set({ unit: e.target.value })}>
              <option value="g">Grams</option>
              <option value="oz">Troy ounces</option>
            </select>
          </div>
        </div>
      )}

      {h.kind === "cash" ? (
        <>
          <div>
            <span className={label}>Name</span>
            <input
              className="nest-input w-full"
              placeholder="e.g. Emergency savings"
              value={h.name}
              onChange={(e) => set({ name: e.target.value })}
              data-testid="holding-name-input"
            />
          </div>
          <div>
            <span className={label}>Value (£)</span>
            <input
              className="nest-input w-full tabular"
              type="number"
              inputMode="decimal"
              step="0.01"
              value={h.amount}
              onChange={(e) => set({ amount: e.target.value })}
              data-testid="holding-amount-input"
            />
          </div>
        </>
      ) : (
        <>
          <div>
            <span className={label}>
              {h.kind === "stock" ? "Units held" : h.kind === "metal" ? `Amount (${h.unit === "g" ? "grams" : "oz"})` : "Amount held"}
            </span>
            <input
              className="nest-input w-full tabular"
              type="number"
              inputMode="decimal"
              step="any"
              value={h.quantity}
              onChange={(e) => set({ quantity: e.target.value })}
              data-testid="holding-quantity-input"
            />
          </div>
          <div>
            <span className={label}>Label (optional)</span>
            <input
              className="nest-input w-full"
              placeholder="Shown instead of the full name"
              value={h.name}
              onChange={(e) => set({ name: e.target.value })}
            />
          </div>
        </>
      )}

      <div className="flex items-center gap-3 pt-1">
        <button
          type="submit"
          disabled={!ready}
          className="nest-btn-primary flex-1 py-3 disabled:opacity-50"
          data-testid="holding-save-btn"
        >
          Save
        </button>
        {onDelete && (
          <button type="button" onClick={onDelete} className="text-sm text-nest-warn px-3">
            Remove
          </button>
        )}
      </div>
    </form>
  );
}
